/**
 * Centralized help content for contextual tooltips across all dashboard components.
 * Keys follow module.component.element convention.
 * Written for a C-level / non-technical audience.
 */

interface HelpEntry {
  title: string;
  description: string;
  /**
   * Optional Help Center anchor (e.g. "crs", "big-six", "poverty"). When
   * set, the tooltip renders a "Learn more" link pointing to
   * `/help#<anchor>` so users can drop into the full methodology section
   * without leaving their flow. The 7 canonical anchors are defined by
   * the SECTIONS array in `shared/components/HelpCenter.jsx`.
   */
  anchor?: string;
}

export const helpContent: Record<string, HelpEntry> = {

  // ============================================================
  // Compass Module — CyberRiskSignal
  // ============================================================
  'compass.crs.score': {
    title: 'Cyber Risk Signal (CRS)',
    description: 'A composite score from 0\u2013100 measuring your organization\u2019s overall cyber risk exposure. Higher scores mean greater risk. The CRS combines external threats, internal vulnerabilities, governance gaps, and organizational stress into one board-ready number.',
    anchor: 'crs',
  },
  'compass.crs.band': {
    title: 'Risk Band',
    description: 'Your CRS score falls into a risk band: Stable, Elevated, High, or Critical. Each band indicates the urgency of executive attention and resource allocation needed.',
    anchor: 'crs',
  },
  'compass.crs.momentum': {
    title: 'Momentum',
    description: 'Shows whether your risk is trending up, down, or holding steady over recent periods. Rising momentum signals worsening conditions that may need intervention.',
    anchor: 'crs',
  },
  'compass.crs.peerComparison': {
    title: 'Peer Comparison',
    description: 'How your CRS compares to organizations of similar size and industry. A lower percentile means you carry less risk than your peers.',
    anchor: 'crs',
  },
  'compass.crs.components': {
    title: 'CRS Components',
    description: 'The five factors that drive your CRS: External Pressure (threats from outside), Internal Fragility (vulnerabilities within), Governance Risk (policy and oversight gaps), Trajectory (direction of change), and Organizational Stress (capacity and culture strain).',
    anchor: 'crs',
  },

  // ============================================================
  // Compass Module — Executive Summary Banner
  // ============================================================
  'compass.executive.banner': {
    title: 'Executive Summary',
    description: 'A high-level snapshot of your most important risk metrics. Use this as a quick health check before diving into specific areas.',
  },
  'compass.executive.criticalRisks': {
    title: 'Critical Risks',
    description: 'The number of risk factors currently rated at critical severity. These require immediate executive attention and may have direct financial or operational impact.',
  },
  'compass.executive.riskVelocity': {
    title: 'Risk Velocity',
    description: 'How quickly your overall risk posture is changing. High velocity means conditions are shifting rapidly and decisions should not be deferred.',
  },
  'compass.executive.impactLevel': {
    title: 'Impact Level',
    description: 'The estimated business impact if current risk exposures materialize. This factors in financial loss, operational disruption, and reputational damage.',
  },

  // ============================================================
  // Compass Module — Cyber Poverty Line
  // ============================================================
  'compass.povertyLine.chart': {
    title: 'Security Investment Curve',
    description: 'Shows the relationship between cybersecurity spending and risk reduction. Below the Poverty Line, your investment is too low to meaningfully reduce risk. The Optimal Zone is where each dollar delivers the best return. Beyond that, you hit diminishing returns.',
    anchor: 'poverty',
  },
  'compass.povertyLine.threshold': {
    title: 'Cyber Poverty Line',
    description: 'The minimum cybersecurity investment needed to achieve baseline protection. Organizations spending below this threshold are significantly more vulnerable to breaches regardless of how well they allocate those funds.',
    anchor: 'poverty',
  },
  'compass.povertyLine.optimalZone': {
    title: 'Optimal Investment Zone',
    description: 'The spending range where you get the best risk reduction per dollar. Staying within this zone maximizes the return on your security investment.',
    anchor: 'poverty',
  },

  // ============================================================
  // Compass Module — Revenue at Risk
  // ============================================================
  'compass.revenueAtRisk.total': {
    title: 'Revenue at Risk',
    description: 'The estimated financial exposure if a cyber incident occurs, based on your organization\u2019s revenue, industry benchmarks, and current security posture.',
  },
  'compass.revenueAtRisk.recoveryTime': {
    title: 'Recovery Time',
    description: 'Estimated time to restore normal operations after a significant cyber incident, considering your current response readiness and backup capabilities.',
  },
  'compass.revenueAtRisk.incidentProbability': {
    title: 'Incident Probability',
    description: 'The likelihood of experiencing a material cyber incident within the next 12 months, based on your threat profile, industry data, and current controls.',
  },

  // ============================================================
  // Compass Module — Big 6 Pillars (360s)
  // ============================================================
  'compass.cyber360': {
    title: 'Cyber360\u2122',
    description: 'A 24-point assessment of your technology risk across four quadrants: Cyber Intel, Security, Management, and Enterprise. This measures how well your technical defenses align with the threats you face.',
    anchor: 'three-sixty',
  },
  'compass.people360': {
    title: 'People360\u2122',
    description: 'Assesses human-factor risk across People, Policy, Technical Controls, and Physical Security. Even the best technology fails if people aren\u2019t trained, policies aren\u2019t followed, or physical access isn\u2019t controlled.',
    anchor: 'three-sixty',
  },
  'compass.data360': {
    title: 'Data360\u2122',
    description: 'Evaluates how well you protect and manage your data across four dimensions: Governance, Security, Quality, and Access. Data is typically an organization\u2019s most valuable and most targeted asset.',
    anchor: 'three-sixty',
  },
  'compass.enterprise360': {
    title: 'Enterprise360\u2122',
    description: 'A strategic view of organizational risk across Governance, Operations, People, and Technology. This pillar looks beyond technical controls to assess whether the business itself is structured to manage cyber risk.',
    anchor: 'three-sixty',
  },

  // ============================================================
  // Compass Module — KRI and Trends
  // ============================================================
  'compass.kri': {
    title: 'Key Risk Indicators',
    description: 'Measurable signals that warn of increasing risk exposure. Each indicator has a threshold \u2014 when breached, it flags a condition requiring investigation or action.',
  },
  'compass.riskTrend': {
    title: 'Geopolitical Hotspots',
    description: 'Regions where cyber threat activity is elevated based on current geopolitical conditions. Organizations with operations or supply chains in these areas should assess their exposure.',
  },
  'compass.threatLandscape': {
    title: 'External Threat Landscape',
    description: 'A real-time view of outside pressures affecting your organization: regulatory changes, threat actor activity, industry-specific risks, and peer benchmark data. These factors are beyond your direct control but must inform your security strategy.',
    anchor: 'outside',
  },

  // ============================================================
  // Defense Module — Readiness & Friction
  // ============================================================
  'defense.spiderChart': {
    title: 'Readiness Spider Chart',
    description: 'A radar view of your security readiness across six dimensions. The wider the shape, the more balanced your posture. Dips in the chart highlight areas needing attention. Compare your original assessment against simulated improvements.',
  },
  'defense.readiness.governance': {
    title: 'Governance',
    description: 'Policies, procedures, and oversight frameworks that guide security decisions. Strong governance ensures consistent, accountable security practices across the organization.',
  },
  'defense.readiness.culture': {
    title: 'Culture',
    description: 'Security awareness and behavioral patterns across your workforce. Culture determines whether security policies are followed in practice, not just on paper.',
  },
  'defense.readiness.budget': {
    title: 'Budget',
    description: 'Investment in security tools, services, and resources. This reflects whether funding matches the risk exposure your organization faces.',
  },
  'defense.readiness.technicalDebt': {
    title: 'Technical Debt',
    description: 'Legacy systems and deferred maintenance that create security gaps. Lower technical debt means fewer exploitable weaknesses. This is an inverse scale \u2014 less is better.',
  },
  'defense.readiness.thirdParty': {
    title: 'Third-Party Risk',
    description: 'Vendor management and supply chain security. Your security is only as strong as the weakest link in your vendor ecosystem.',
  },
  'defense.readiness.people': {
    title: 'People',
    description: 'Staffing levels and security expertise. Understaffed or undertrained teams cannot sustain effective security operations regardless of tool investments.',
  },
  'defense.goldilocks': {
    title: 'Goldilocks ROI Zones',
    description: 'Shows the optimal readiness range for each dimension \u2014 not too little (high risk), not too much (wasted spend). Staying in the \u201cjust right\u201d zone maximizes risk reduction per dollar and minimizes business friction.',
    anchor: 'goldilocks',
  },

  // ============================================================
  // Defense Module — Simulation & Outside Pressures
  // ============================================================
  'defense.simulation': {
    title: 'Financial Impact Analysis',
    description: 'Uses Monte Carlo modeling to simulate thousands of potential incident scenarios against your revenue and budget. This shows the probable range of financial losses so you can make investment decisions backed by data, not guesswork.',
  },
  'defense.outsidePressures': {
    title: 'Outside Pressures Analysis',
    description: 'External factors that affect your cyber risk but are outside your direct control: regulatory pressure, geopolitical instability, industry threat trends, and supply chain risks. Your strategy must account for these forces.',
    anchor: 'outside',
  },

  // ============================================================
  // Board Reports — Dartboard
  // ============================================================
  'boardReports.dartboard': {
    title: 'Dartboard 360 View',
    description: 'A radial visualization showing risk scores across all assessment pillars. Each sector represents a domain. Inner rings indicate higher risk; outer rings indicate stronger readiness. Use this for at-a-glance board reporting.',
  },

  // ============================================================
  // Foundations — Assessment Dashboard
  // ============================================================
  'foundations.assessmentDashboard': {
    title: 'Assessment Results',
    description: 'Your comprehensive risk assessment results. Each section breaks down a different dimension of your organization\u2019s cybersecurity readiness, from technical controls to business alignment. Drill into any card for detailed findings and recommendations.',
  },
  'foundations.big6Bars': {
    title: 'Optimization Gaps',
    description: 'Each bar shows how far you are from the optimal range for your industry. Longer bars mean a bigger gap \u2014 either under-invested or over-invested. No bar (green "Optimized" label) means you\u2019re in the Goldilocks zone. A large Budget gap means additional investment would have outsized impact, which is why Security Investment ROI may show "High Return" at the same time.',
    anchor: 'big-six',
  },
  'foundations.investmentROI': {
    title: 'Security Investment ROI',
    description: 'The return-on-investment multiplier for your current security spending. A high ROI means each dollar is working hard \u2014 but it can also signal underfunding. When paired with red Big 6 bars, "High Return" means there is significant room to improve with relatively modest additional investment.',
  },
  'foundations.revenueAtRiskCard': {
    title: 'Revenue at Risk',
    description: 'The estimated financial exposure if a cyber incident occurs, based on your company\u2019s revenue, industry benchmarks, and current security gaps. Recovery days estimate how long it would take to restore normal operations.',
  },

  // ============================================================
  // Compass — Annual Risk Budget (replaces legacy tooltip)
  // ============================================================
  'compass.executive.riskBudget': {
    title: 'Annual Risk Budget',
    description: 'The recommended annual cybersecurity investment based on your organization\u2019s risk profile, revenue, and industry benchmarks. This is calculated from your assessment data, not an industry average.',
  },

  // ============================================================
  // Compass / Business Intelligence \u2014 Optimization Level card
  // ============================================================
  'compass.optimizationLevel': {
    title: 'Optimization Level',
    description: 'Composite signal of how prepared your organization is to optimize cybersecurity investment. Derived from your Security Readiness score \u2014 a self-report from assessment responses (or 100 minus your Risk Score when readiness isn\u2019t available). Bands: High \u226565 (most controls reportedly in place \u2014 focus on tuning and de-duplication), Moderate \u226545 (real gaps remain \u2014 sequence by Big 6 priority), Low <45 (foundational work first). Pair with your Big 6 bars; \u201cHigh\u201d here next to red Big 6 dimensions usually indicates optimistic self-assessment.',
  },

  // ============================================================
  // Foundations \u2014 Evidence Intelligence card
  // ============================================================
  'foundations.evidenceIntelligence': {
    title: 'Evidence Intelligence',
    description: 'PRISM AI analysis of the policy documents you\u2019ve synced from Foundations. Iris reads each document and scores it against compliance requirements, producing per-requirement coverage (Fully / Partially / Not Covered) and gap-by-gap guidance on what evidence would close each gap. Use the gap-guidance table to prioritize next-document uploads \u2014 adding evidence cited there has the largest effect on overall Coverage %.',
  },
};
